/**
 * 그래프 공용 셈(그리는 코드와 떼어 둔 순수 함수). 라이브러리를 바꾸더라도 눈금 · 글자 너비 규칙은 여기 하나다.
 */

/** 한 눈금 간격의 후보(× 10ⁿ). 1.2 · 1.5 · 3 · 4 · 6 · 8 도 두어 맨 위 눈금이 자료보다 지나치게 커지지 않게 한다 */
const NICE_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10] as const;

/** 부동소수 찌꺼기(0.30000000000000004)를 없앤다 */
const clean = (value: number) => Number(value.toPrecision(12));

export interface LinearScale {
  /** 세로축 맨 아래 · 맨 위 값(눈금과 같다) */
  min: number;
  max: number;
  /** 아래에서 위로. 0 이 범위 안에 있으면 0 도 눈금이다 */
  ticks: number[];
}

/**
 * 눈금 `intervals + 1` 개(기본 5개)의 보기 좋은 축. 모든 눈금은 간격의 배수라 0 이 범위 안이면 0 이 눈금이 된다.
 * 범위가 없으면(min = max) 위로 1 을 넓힌다.
 */
export function niceScale(min: number, max: number, intervals = 4): LinearScale {
  const low = Number.isFinite(min) ? min : 0;
  const high = Number.isFinite(max) && max > low ? max : low + 1;
  const raw = (high - low) / intervals;
  const power = 10 ** Math.floor(Math.log10(raw));
  for (const scale of [power, power * 10, power * 100]) {
    for (const nice of NICE_STEPS) {
      const step = clean(nice * scale);
      if (step < raw * (1 - 1e-9)) continue;
      const start = clean(Math.floor(low / step + 1e-9) * step);
      const end = clean(start + step * intervals);
      if (end >= high * (1 - 1e-9) - 1e-9) {
        const ticks = Array.from({ length: intervals + 1 }, (_, index) =>
          clean(start + step * index),
        );
        return { min: start, max: end, ticks };
      }
    }
  }
  // 닿지 않는다(위 반복이 항상 끝난다) — 형식만 맞춘다
  return { min: low, max: high, ticks: [low, high] };
}

/**
 * 시계열 세로축의 자료 범위 — 기존 chart.js 와 같다(`lib/chart-js.ts`):
 * 위 = floor(max(10, 1.1 × 최댓값)), 아래 = floor(min(0, 최솟값)). 그 뒤 `niceScale` 로 눈금을 둥글린다.
 */
export function valueDomain(values: readonly (number | null)[]): { min: number; max: number } {
  const numbers = values.filter(
    (value): value is number => value !== null && Number.isFinite(value),
  );
  const top = numbers.length === 0 ? 0 : Math.max(...numbers);
  const bottom = numbers.length === 0 ? 0 : Math.min(...numbers);
  return { min: Math.floor(Math.min(0, bottom)), max: Math.floor(Math.max(10, 1.1 * top)) };
}

/** 값 → 세로 좌표(px). 위가 작은 수(SVG) */
export function scaleY(scale: LinearScale, top: number, height: number) {
  const span = scale.max - scale.min || 1;
  return (value: number) => top + ((scale.max - value) / span) * height;
}

/**
 * 글자 너비 어림(px). 재지 않고 셈한다(그리기 전 · 테스트 환경에서도 같다).
 * 한글 · 한자 등은 글자 크기만큼, 영문 · 숫자는 그 0.58 배, 쉼표 · 마침표 · 공백은 0.3 배.
 */
export function estimateTextWidth(text: string, fontSize = 12): number {
  let width = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code >= 0x1100) width += fontSize;
    else if (char === ',' || char === '.' || char === ' ' || char === '(' || char === ')')
      width += fontSize * 0.3;
    else width += fontSize * 0.58;
  }
  return Math.ceil(width);
}

/** 선명한 1px 선의 좌표(반 픽셀에 놓는다) */
export const crisp = (value: number) => Math.round(value) + 0.5;
